import React, { useState } from 'react';
import {
    Modal, Box, Typography, Button, TextField, IconButton, Grid, Divider, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MarkedText from './MarkedText';

const critSymbols = (count) => "[AD]".repeat(count || 0);

const STAT_COLORS = { red: '#670d0d', blue: '#021c61' };
const Stat = ({ label, value, color }) => (
    <Paper variant="outlined" sx={{ textAlign: 'center', overflow: 'hidden', height: '100%' }}>
        <Box sx={{ bgcolor: STAT_COLORS[color], color: 'white', px: 0.25 }}>
            <Typography component="div" sx={{ fontSize: { xs: '0.6rem', sm: '0.72rem' }, lineHeight: 1.15, fontWeight: 600, overflowWrap: 'anywhere' }}>
                {label}
            </Typography>
        </Box>
        <Typography component="div" sx={{ fontSize: { xs: '1.05rem', sm: '1.35rem' }, fontWeight: 700, py: 0.25 }}>
            {value}
        </Typography>
    </Paper>
);

const skillLine = (profile) => {
    const { Skills, AdversaryType } = profile;
    if (!Skills) return null;
    if (Array.isArray(Skills)) return Skills.join(', ');
    return Object.entries(Skills)
        .map(([name, rank]) => (AdversaryType !== 'Minion' && rank ? `${name} ${rank}` : name))
        .join(', ');
};

const NpcProfilePreview = ({ npc }) => {
    const p = npc.Profile;
    const c = p.Characteristics || {};
    const attrs = p.Attributes || {};
    const chars = [['Brawn', 'BR'], ['Agility', 'AG'], ['Intellect', 'INT'], ['Cunning', 'CUN'], ['Willpower', 'WIL'], ['Presence', 'PR']];

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6">{npc.Name}</Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {p.AdversaryType}{npc.Source ? ` • ${npc.Source}` : ''}
            </Typography>
            {npc.Description && (
                <Box sx={{ textAlign: 'left', mb: 1 }}
                    dangerouslySetInnerHTML={{ __html: MarkedText.renderer(npc.Description) }} />
            )}

            <Grid container spacing={0.5}>
                {chars.map(([label, key]) => (
                    <Grid item xs={4} key={key}>
                        <Stat label={label} value={c[key] ?? 0} color="red" />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={0.5} sx={{ mt: 0.25, alignItems: 'stretch' }}>
                <Grid item xs={6} sm={3}>
                    <Stat label="Wounds" value={attrs.WoundThreshold || '-'} color="blue" />
                </Grid>
                {attrs.StrainThreshold != null && (
                    <Grid item xs={6} sm={3}>
                        <Stat label="Strain" value={attrs.StrainThreshold} color="blue" />
                    </Grid>
                )}
                <Grid item xs={6} sm={3}>
                    <Stat label="Soak" value={attrs.Soak ?? 0} color="blue" />
                </Grid>
                {attrs.Defense && (
                    <Grid item xs={6} sm={3}>
                        <Stat label="Defense M/R" value={`${attrs.Defense[0]} / ${attrs.Defense[1]}`} color="blue" />
                    </Grid>
                )}
            </Grid>

            {p.Silhouette != null && p.Silhouette !== 1 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>Silhouette: {p.Silhouette}</Typography>
            )}

            <Box sx={{ textAlign: 'left', mt: 1 }}>
                {skillLine(p) && (
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>Skills:</strong> {skillLine(p)}</Typography>
                )}
                {p.Talents && p.Talents.length > 0 && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Talents:</strong> {p.Talents.map((t) => (t && t.Name) || t).join(', ')}
                    </Typography>
                )}
                {p.Abilities && p.Abilities.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Abilities:</strong></Typography>
                        {p.Abilities.map((ab, i) => (
                            <Box key={i} sx={{ fontSize: '0.875rem' }}
                                dangerouslySetInnerHTML={{
                                    __html: MarkedText.renderer(ab && ab.Description ? `${ab.Name}: ${ab.Description}` : ab)
                                }} />
                        ))}
                    </Box>
                )}
                {p.Weapons && p.Weapons.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Weapons:</strong></Typography>
                        {p.Weapons.map((w, i) => (
                            <Paper key={i} variant="outlined" sx={{ p: 1, mb: 0.5 }}>
                                <Typography variant="subtitle2">{w.Name}</Typography>
                                {(w.Skill || w.Damage != null) && (
                                    <Typography variant="body2">
                                        {w.Skill ? `Skill: ${w.Skill}` : ''}{w.Skill && w.Damage != null ? '; ' : ''}
                                        {w.Damage != null ? `Damage: ${w.Damage}` : ''}{w.Range ? `; Range: ${w.Range}` : ''}
                                    </Typography>
                                )}
                                {w.Crit != null && (
                                    <Box sx={{ fontSize: '0.875rem' }}
                                        dangerouslySetInnerHTML={{ __html: MarkedText.renderer(`Critical: ${critSymbols(w.Crit)}`) }} />
                                )}
                                {w.Qualities && (
                                    <Box sx={{ fontSize: '0.875rem' }}
                                        dangerouslySetInnerHTML={{ __html: MarkedText.renderer('Qualities: ' + w.Qualities.join(', ')) }} />
                                )}
                            </Paper>
                        ))}
                    </Box>
                )}
                {p.Gear && (
                    <Box sx={{ fontSize: '0.875rem' }}
                        dangerouslySetInnerHTML={{ __html: MarkedText.renderer('Equipment: ' + p.Gear) }} />
                )}
            </Box>
        </Box>
    );
};

const TYPE_FILTERS = [['Droids', 'Droids'], ['Trainable Beasts', 'Creatures'], ['Characters', 'Characters']];
const TIER_FILTERS = ['Nemesis', 'Rival', 'Minion'];

const AddNpcModal = ({ open, handleClose, npcs, onAdd }) => {
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [sourceGroups, setSourceGroups] = useState(new Set(['official']));
    const [types, setTypes] = useState(new Set(['Droids', 'Trainable Beasts']));
    const [tiers, setTiers] = useState(new Set(TIER_FILTERS));

    const toggle = (set, setter) => (val) => {
        const next = new Set(set);
        next.has(val) ? next.delete(val) : next.add(val);
        setter(next);
    };

    const query = search.trim().toLowerCase();
    const filtered = npcs.filter((n) => {
        if (!sourceGroups.has(n.Official ? 'official' : 'community')) return false;
        if (!types.has(n.Type)) return false;
        if (!tiers.has(n.Profile.AdversaryType)) return false;
        if (!query) return true;
        return n.Name.toLowerCase().includes(query)
            || (n.Source || '').toLowerCase().includes(query)
            || (n.Profile.AdversaryType || '').toLowerCase().includes(query)
            || (n.Type || '').toLowerCase().includes(query);
    });

    const MAX_ROWS = 150;
    const shown = filtered.slice(0, MAX_ROWS);

    const filterChip = (label, active, onClick) => (
        <Chip
            key={label}
            label={label}
            size="small"
            onClick={onClick}
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
        />
    );

    return (
        <Modal open={open} onClose={handleClose} sx={{ overflow: 'auto' }}>
            <Box sx={{
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: { xs: 2, sm: 4 },
                mx: { xs: 1, sm: 'auto' },
                my: { xs: '2vh', sm: '5vh' },
                maxWidth: 1000,
                maxHeight: { xs: '96vh', sm: '90vh' },
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Add NPC</Typography>
                    <IconButton onClick={handleClose} className="no-print"><CloseIcon /></IconButton>
                </Box>
                <TextField
                    label="Search by name, type, or source"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mb: 1.5 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Sources:</Typography>
                    {filterChip('Official', sourceGroups.has('official'), () => toggle(sourceGroups, setSourceGroups)('official'))}
                    {filterChip('Community', sourceGroups.has('community'), () => toggle(sourceGroups, setSourceGroups)('community'))}
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Type:</Typography>
                    {TYPE_FILTERS.map(([value, label]) => filterChip(label, types.has(value), () => toggle(types, setTypes)(value)))}
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Tier:</Typography>
                    {TIER_FILTERS.map((tier) => filterChip(tier, tiers.has(tier), () => toggle(tiers, setTiers)(tier)))}
                </Box>
                <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 0 }}>
                    <Grid item xs={12} md={selected ? 6 : 12} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: 'auto', maxHeight: '60vh' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Source</TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {shown.map((npc) => (
                                        <TableRow
                                            key={npc.Key}
                                            hover
                                            selected={selected && selected.Key === npc.Key}
                                            onClick={() => setSelected(npc)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>{npc.Name}</TableCell>
                                            <TableCell>{npc.Profile.AdversaryType}</TableCell>
                                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{npc.Source}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); onAdd(npc); }}
                                                >
                                                    Add
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filtered.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Typography variant="body2">No NPCs match the current search and filters.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filtered.length > MAX_ROWS && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Showing {MAX_ROWS} of {filtered.length} — refine your search to narrow results.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                    {selected && (
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, overflow: 'auto', maxHeight: '60vh' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                                    <Button variant="contained" size="small" onClick={() => onAdd(selected)}>Add to Gear</Button>
                                </Box>
                                <Divider sx={{ mb: 1 }} />
                                <NpcProfilePreview npc={selected} />
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Modal>
    );
};

export default AddNpcModal;
